import { CrCalendarInput } from "@components/legacy/CrCalendarInput";
import React, { useCallback, useEffect, useMemo } from "react";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CrfDateItemSpec, CrfTimeItemSpec, YmdSegmentedDate } from "maven-lib/dist/consts/edc";
import { hasOwnProperty, removeDbPrefixAndSuffix } from "maven-lib/dist/consts/edc/lib";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { CrDropdown } from "@maven-surface/components/base/CrDropdown";
import { CrYearInput } from "@maven-surface/components/base/CrYearInput";
import { CrYearMonthInput } from "@maven-surface/components/base/CrYearMonthInput";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";
import dayjs from "dayjs";

dayjs.extend(customParseFormat);

const hoursOptions = (unknowable?: boolean): Array<{ key: string; text: string }> => {
  const h: Array<{ key: string; text: string }> = [];
  Array(24)
    .fill(0, 0, 24)
    .forEach((__, hIdx) => h.push({ key: String(hIdx).padStart(2, "0"), text: String(hIdx).padStart(2, "0") }));
  unknowable && h.push({ key: "UK", text: "UK" });

  return h;
};

const minutesOptions = (unknowable?: boolean): Array<{ key: string; text: string }> => {
  const m: Array<{ key: string; text: string }> = [];
  Array(60)
    .fill(0, 0, 60)
    .forEach((__, mIdx) => m.push({ key: String(mIdx).padStart(2, "0"), text: String(mIdx).padStart(2, "0") }));
  unknowable && m.push({ key: "UK", text: "UK" });

  return m;
};

interface QuestionnaireDateItemProps {
  questionnaire: EcoaQuestionnaire;
  item: CrfDateItemSpec;
  disabled?: boolean;
  isAvailable?: boolean;
  defaultItemValues?: Array<{ itemId: string | null; dbVal: string | number | null }>;
  onValueChange: (values: Array<{ itemId: string | null; dbVal: string | number | null }>) => void;
}

export function QuestionnaireDateItem({
  isAvailable = true,
  item,
  disabled,
  defaultItemValues,
  onValueChange,
}: QuestionnaireDateItemProps) {
  const date = defaultItemValues?.find((val) => val.itemId === item.id)?.dbVal || null;
  const childrenState: { [id: string]: { type?: string; val: string | number | null } } = useMemo(
    () =>
      item.children?.items.reduce((prev, acc) => {
        const time = defaultItemValues?.find((val) => val.itemId === (acc as CrfTimeItemSpec).id);

        if (time?.itemId)
          return {
            ...prev,
            [time.itemId]: {
              type: acc?.type,
              val: removeDbPrefixAndSuffix(time.dbVal, {
                dbPrefix: (acc as CrfTimeItemSpec).dbPrefix,
                dbSuffix: (acc as CrfTimeItemSpec).dbSuffix,
              }),
            },
          };
        else return prev;
      }, {}) || {},
    [defaultItemValues, item.children?.items]
  );

  const handleCalendarInputChangeDate = (val: string) => {
    const formattedDbVal: string | null = (() => {
      if (!item.format || item.format === "YYYY/MM/DD") {
        return val.split("/").join("");
      } else if (item.format === "YYYY-MM-DD") {
        return val.split("-").join("");
      } else if (item.format === "DD-MMM-YYYY") {
        // TODO: be 쪽도 UNK는 캡쳐되지 않도록 validation 추가 필요
        if (val.includes("UNK")) {
          return `${val.split("-").reverse().join("").replace("UNK", "UK")}`;
        } else if (val.includes("UK")) {
          return `${val.split("-").join("").slice(5, 9)}${(
            "00" +
            (
              dayjs
                .localeData()
                .monthsShort()
                .findIndex((name) => name === val.slice(3, 6)) + 1
            ).toString()
          ).slice(-2)}${val.slice(0, 2)}`;
        } else {
          return `${val.split("-").join("").slice(5, 9)}${dayjs(val, "dd-MMM-YYYY").format("MM")}${val.slice(0, 2)}`;
        }
      } else if (item.format === "DD/MMM/YYYY") {
        // TODO: be 쪽도 UNK는 캡쳐되지 않도록 validation 추가 필요
        if (val.includes("UNK")) {
          return `${val.split("/").reverse().join("").replace("UNK", "UK")}`;
        } else if (val.includes("UK")) {
          return `${val.split("/").join("").slice(5, 9)}${(
            "00" +
            (
              dayjs
                .localeData()
                .monthsShort()
                .findIndex((name) => name === val.slice(3, 6)) + 1
            ).toString()
          ).slice(-2)}${val.slice(0, 2)}`;
        } else {
          return `${val.split("/").join("").slice(5, 9)}${dayjs(val, "dd/MMM/YYYY").format("MM")}${val.slice(0, 2)}`;
        }
      } else if (item.format === "YYYY") {
        return val;
      }

      return null;
    })();

    onValueChange([{ itemId: item.id, dbVal: formattedDbVal }]);
  };

  const handleMonthInputChangeDate = (val: string) => {
    const formattedDbVal: string | null = (() => {
      if (!item.format || item.format === "YYYY/MM") {
        return val.split("/").join("");
      } else if (item.format === "YYYY-MM") {
        return val.split("-").join("");
      } else if (item.format === "MMM/YYYY") {
        if (val.includes("UNK")) {
          return `${val.split("/").reverse().join("").replace("UNK", "UK")}`;
        } else if (val.includes("UK")) {
          return `UKUK${dayjs(val.slice(0, 3).toString(), "MMM").format("MM")}`;
        }

        return val.split("/").join("");
      }

      return null;
    })();

    onValueChange([{ itemId: item.id, dbVal: formattedDbVal }]);
  };

  const handleChangeTime = (val: string | number | null, itemId: string, timeType: "HOUR" | "MINUTE", delimiter?: string) => {
    if (!["HOUR", "MINUTE"].includes(timeType)) return;

    const [hour, minute] = String(childrenState[itemId]?.val ?? "").split(delimiter || "");

    let timeVal = null;
    if (timeType === "HOUR") {
      timeVal = `${val}${delimiter || ""}${minute || "00"}`;
    } else if (timeType === "MINUTE") {
      timeVal = `${hour || "00"}${delimiter || ""}${val}`;
    }
    onValueChange([{ itemId, dbVal: timeVal }]);
  };

  const handleCalendarInputRemoveDate = () => {
    init();
  };

  const getTimeValues = (
    itemId: string,
    delimiter?: string
  ): {
    hour?: string | undefined;
    minute?: string | undefined;
  } => {
    const timeValue = childrenState[itemId];

    if (!timeValue?.val) return {};

    const [hour, minute] = String(timeValue.val).split(delimiter || "");

    return { hour: String(hour || 0).padStart(2, "0"), minute: minute || "00" };
  };

  const init = useCallback(() => {
    const childrenValues =
      item.children?.items.reduce((prev, acc) => {
        const time = defaultItemValues?.find((val) => val.itemId === (acc as CrfTimeItemSpec).id);

        if (time?.itemId)
          return {
            ...prev,
            [time.itemId]: {
              type: acc?.type,
              val: null,
            },
          };
        else return prev;
      }, {}) || {};

    onValueChange([{ itemId: item.id, dbVal: null }, ...Object.keys(childrenValues).map((childId) => ({ itemId: childId, dbVal: null }))]);
  }, [defaultItemValues, item.children?.items, item.id, onValueChange]);

  useEffect(() => {
    if (
      !isAvailable &&
      (date !== null || Object.keys(childrenState).some((cId) => childrenState[cId].val !== null)) &&
      !item.ecoa?.noSkipWhenDisabled
    ) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenState, date, init, isAvailable]);

  const convertDate = (itemDate: number | "TODAY" | YmdSegmentedDate) => {
    if (hasOwnProperty(itemDate, "year")) {
      const date = dayjs(`${itemDate.year}-${itemDate.month}-${itemDate.day}`, "YYYY-MM-DD", true);

      return date.isValid() ? date.toDate() : undefined;
    }

    return itemDate;
  };

  return (
    <>
      {(!item.format ||
        item.format === "YYYY/MM/DD" ||
        item.format === "YYYY-MM-DD" ||
        item.format === "DD-MMM-YYYY" ||
        item.format === "DD/MMM/YYYY") && (
        <CrCalendarInput
          disabled={disabled}
          allUnknownable={item.unknowable?.all}
          yearUnknownable={item.unknowable?.year}
          monthUnknownable={item.unknowable?.month}
          monthDayUnknownable={item.unknowable?.monthDay}
          dayUnknownable={item.unknowable?.day}
          minDate={convertDate(item.minDate)}
          maxDate={convertDate(item.maxDate)}
          typeable={item.typeable}
          format={item.format || "YYYY/MM/DD"}
          defaultValue={date as string}
          onValueChange={handleCalendarInputChangeDate}
          onDeleteDate={handleCalendarInputRemoveDate}
        />
      )}
      {(item.format === "YYYY/MM" || item.format === "YYYY-MM" || item.format === "MMM/YYYY") && (
        <CrYearMonthInput
          disabled={disabled}
          allUnknownable={!!item.unknowable?.all}
          yearUnknownable={!!item.unknowable?.year}
          monthUnknownable={!!item.unknowable?.month}
          minDate={convertDate(item.minDate)}
          maxDate={convertDate(item.maxDate)}
          format={item.format || "YYYY/MM"}
          defaultValue={date as string}
          onValueChange={handleMonthInputChangeDate}
          onDeleteDate={handleCalendarInputRemoveDate}
        />
      )}
      {item.format === "YYYY" && (
        <CrYearInput
          disabled={disabled}
          defaultValue={date as string}
          minDate={convertDate(item.minDate)}
          maxDate={convertDate(item.maxDate)}
          onValueChange={handleCalendarInputChangeDate}
          onDeleteDate={handleCalendarInputRemoveDate}
        />
      )}
      {item.children?.items.map((item, itemIdx) => {
        return item.type === "TIME" ? (
          <div className="app-ecoa-time-wrapper" key={`children-time-${itemIdx}`}>
            <div className="--item-time">
              <CrDropdown
                placeholder={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_HOUR)}
                key={`${date}_hour`}
                defaultKey={getTimeValues(item.id, item.delimiter).hour}
                disabled={disabled}
                onValueChange={(val) => handleChangeTime(val, item.id, "HOUR", item.delimiter)}
                items={hoursOptions(item.unknowable?.hour)}
              />
              <CrDropdown
                placeholder={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_MIN)}
                key={`${date}_minute`}
                defaultKey={getTimeValues(item.id, item.delimiter).minute}
                disabled={disabled}
                onValueChange={(val) => handleChangeTime(val, item.id, "MINUTE", item.delimiter)}
                items={minutesOptions(item.unknowable?.minute)}
              />
            </div>
          </div>
        ) : (
          <></>
        );
      })}
    </>
  );
}
